import { useMemo } from 'react';

export type RowSpanInfo = { rowSpan: number; isFirst: boolean };

/** 任务列表行记录中用于合并的字段 */
export interface TaskRecordForRowSpan {
    routeId?: string | number | null;
    routeName?: string | null;
    routeCode?: string | null;
    taskDate?: string | null;
}

/**
 * 计算任务列表表格中路线列、任务日期列的 rowSpan，用于合并相同 routeId、routeName、routeCode（及 taskDate）的单元格
 * ShipFromHereTask 和 ShipToHereTask 共用
 */
export const useTaskRouteRowSpan = (taskData: TaskRecordForRowSpan[]) => {
    // 计算路线列的 rowSpan，按 routeId、routeName、routeCode 一起合并，三者都相同才合并
    const routeRowSpanMap = useMemo(() => {
        const map = new Map<number, RowSpanInfo>();
        if (taskData.length === 0) return map;

        let currentRouteId: string | number | null = null;
        let currentRouteName: string | null = null;
        let currentRouteCode: string | null = null;
        let startIndex = 0;
        let count = 0;

        taskData.forEach((record, index) => {
            const routeId = record.routeId;
            const routeName = record.routeName ?? null;
            const routeCode = record.routeCode ?? null;
            if (routeId === currentRouteId && routeName === currentRouteName && routeCode === currentRouteCode) {
                count++;
            } else {
                if (currentRouteId !== null && currentRouteName !== null && count > 0) {
                    map.set(startIndex, { rowSpan: count, isFirst: true });
                    for (let i = startIndex + 1; i < startIndex + count; i++) {
                        map.set(i, { rowSpan: 0, isFirst: false });
                    }
                }
                currentRouteId = routeId ?? null;
                currentRouteName = routeName;
                currentRouteCode = routeCode;
                startIndex = index;
                count = 1;
            }
        });

        if (currentRouteId !== null && currentRouteName !== null && count > 0) {
            map.set(startIndex, { rowSpan: count, isFirst: true });
            for (let i = startIndex + 1; i < startIndex + count; i++) {
                map.set(i, { rowSpan: 0, isFirst: false });
            }
        }

        return map;
    }, [taskData]);

    // 计算任务日期列的 rowSpan，按 routeId、routeName、routeCode、taskDate 一起合并
    const taskDateRowSpanMap = useMemo(() => {
        const map = new Map<number, RowSpanInfo>();
        if (taskData.length === 0) return map;

        let currentRouteId: string | number | null = null;
        let currentRouteName: string | null = null;
        let currentRouteCode: string | null = null;
        let currentTaskDate: string | null = null;
        let startIndex = 0;
        let count = 0;

        taskData.forEach((record, index) => {
            const routeId = record.routeId;
            const routeName = record.routeName ?? null;
            const routeCode = record.routeCode ?? null;
            const taskDate = record.taskDate ?? null;
            if (routeId === currentRouteId && routeName === currentRouteName && routeCode === currentRouteCode && taskDate === currentTaskDate) {
                count++;
            } else {
                if (currentRouteId !== null && currentRouteName !== null && currentTaskDate !== null && count > 0) {
                    map.set(startIndex, { rowSpan: count, isFirst: true });
                    for (let i = startIndex + 1; i < startIndex + count; i++) {
                        map.set(i, { rowSpan: 0, isFirst: false });
                    }
                }
                currentRouteId = routeId ?? null;
                currentRouteName = routeName;
                currentRouteCode = routeCode;
                currentTaskDate = taskDate;
                startIndex = index;
                count = 1;
            }
        });

        if (currentRouteId !== null && currentRouteName !== null && currentTaskDate !== null && count > 0) {
            map.set(startIndex, { rowSpan: count, isFirst: true });
            for (let i = startIndex + 1; i < startIndex + count; i++) {
                map.set(i, { rowSpan: 0, isFirst: false });
            }
        }

        return map;
    }, [taskData]);

    return { routeRowSpanMap, taskDateRowSpanMap };
};
